import {
	useContext,
	ReactNode,
	useEffect,
	useState,
	createContext,
	RefAttributes,
} from 'react';
import { useQuery } from '@tanstack/react-query';
import { useLocalStorage } from '@mantine/hooks';
import { ApiErrorMessage, Language, Translations } from '@/types/types';
import { isAppError, isDefaultLanguage } from '@/utils/utilities';
import { ErrorCode } from '@/configs/errorCode';
import httpClient from '@/api/httpClient';
import parse, { Element, domToReact } from 'html-react-parser';
import { IconBulb, IconCheck, IconInfoCircle } from '@tabler/icons-react';
import ContentApi, { Content, ContentType } from '@/api/ContentApi';
import APP_CONFIG from '@/configs/appConfig';
import moment from 'moment';
import 'moment/dist/locale/pl';
import 'moment/dist/locale/en-gb';
import 'moment/dist/locale/de';

export type AppContents = Record<string, Content>;

export interface ConentContext {
	getContent: (
		slug: string,
		options?: {
			links?: Record<string, RefAttributes<HTMLAnchorElement>>;
			vars?: Record<string, string | number>;
		}
	) => string | ReactNode;
	hasContent: (slug: string) => boolean;
	language: Language;
	setLanguage: (lang: Language) => void;
	getErrorMessage: (codes: ApiErrorMessage) => string;
	getMutationErrorMessage: (error: unknown) => string;
	getEntityText: <
		TObj extends {
			translations: Translations<Partial<Record<keyof TObj, string>>> | null;
		}
	>(
		obj: TObj,
		key: keyof TObj
	) => string;
}

const defaultContext: ConentContext = {
	getContent: (slug) => slug,
	hasContent: () => false,
	language: APP_CONFIG.DEFAULT_LANGUAGE,
	setLanguage: () => undefined,
	getErrorMessage: () => '',
	getMutationErrorMessage: () => '',
	getEntityText: (obj, key) => obj[key] as string,
};

const ContentContext = createContext<ConentContext>(defaultContext);

export const useContent = () => useContext(ContentContext);

export const ContentProvider = ({ children }: { children: ReactNode }) => {
	const [content, setContent] = useLocalStorage<AppContents>({
		key: 'content',
		defaultValue: {},
		getInitialValueInEffect: false,
	});

	const [language, setLanguage] = useLocalStorage<Language>({
		key: 'language',
		defaultValue: APP_CONFIG.DEFAULT_LANGUAGE,
		getInitialValueInEffect: false,
	});

	const isDefault = language === APP_CONFIG.DEFAULT_LANGUAGE;

	const [_, setRefresher] = useState(false);
	const refreshApp = () => setRefresher((prev) => !prev);

	useEffect(() => {
		moment.locale(language.toLowerCase());
		document.documentElement.setAttribute('lang', language);
		httpClient.defaults.headers['Accept-Language'] = language;
		refreshApp();
	}, [language]);

	const getEntityText: ConentContext['getEntityText'] = (obj, key) => {
		if (isDefaultLanguage(language)) return obj[key] as string;

		return !!obj.translations &&
			!!obj.translations[language] &&
			!!obj.translations[language][key]
			? (obj.translations[language][key] as string)
			: (obj[key] as string);
	};

	const checkIfExist = (slug: string) => {
		if (!content[slug]) return false;

		if (content[slug].type === ContentType.IMAGE) {
			const imagePath = isDefault
				? content[slug].image?.path
				: content[slug].translations?.[language]?.image?.path ||
					content[slug].image?.path;
			return !!imagePath;
		}

		const text = isDefault
			? content[slug].content
			: content[slug].translations?.[language]?.content ||
				content[slug].content;
		return !!text;
	};

	const contentQuery = useQuery({
		queryKey: [ContentApi.queryKey],
		queryFn: ContentApi.getAll,
		staleTime: 1000 * 60 * 5,
		select: (contents) =>
			contents.reduce<AppContents>((acc, curr) => {
				acc[curr.slug] = curr;
				return acc;
			}, {}),
	});

	useEffect(() => {
		if (!contentQuery.data) return;

		setContent(contentQuery.data);
	}, [contentQuery.data]);

	const getText = (
		slug: string,
		vars: Record<string, string | number> = {}
	) => {
		let text =
			(isDefault
				? content[slug].content
				: content[slug].translations?.[language]?.content ||
					content[slug].content) ?? '';

		for (const [variable, value] of Object.entries(vars)) {
			const placeholder = `{{${variable}}}`;
			text = text.replaceAll(placeholder, value.toString());
		}

		return text;
	};

	const getElement = (
		slug: string,
		options?: {
			links?: Record<string, RefAttributes<HTMLAnchorElement>>;
			vars?: Record<string, string | number>;
		}
	) => {
		const raw = getText(slug, options?.vars);
		const isHelpContent = slug.startsWith('configurator.help.');

		return parse(raw, {
			replace: (domNode) => {
				const node = domNode as Element;

				if (
					node.name === 'a' &&
					options?.links &&
					options?.links[node.attribs.href]
				) {
					return (
						<a {...options.links[node.attribs.href]}>
							{domToReact(node.children as Element[])}
						</a>
					);
				}

				if (isHelpContent && node.name === 'h3') {
					const headingText = (node.children || [])
						.map((child: any) => (child.type === 'text' ? child.data : ''))
						.join('')
						.trim()
						.toLowerCase();

					const icon =
						headingText.includes('wskazówk') || headingText.includes('tips')
							? <IconBulb size={16} />
							: headingText.includes('opis') ||
							  headingText.includes('informacje') ||
							  headingText.includes('description')
								? <IconInfoCircle size={16} />
								: null;

					if (!icon) return undefined;

					return (
						<h3 className={node.attribs?.class}>
							{icon}
							{domToReact(node.children as Element[])}
						</h3>
					);
				}

				if (isHelpContent && node.name === 'div' && node.attribs?.class?.includes('cfg-panel__tip')) {
					return (
						<div className={node.attribs?.class}>
							<IconCheck size={16} />
							{domToReact(node.children as Element[])}
						</div>
					);
				}
			},
		});
	};

	const getImage = (slug: string) => {
		const imagePath = isDefault
			? content[slug].image?.path
			: content[slug].translations?.[language]?.image?.path ||
				content[slug].image?.path;

		return `${import.meta.env.VITE_API_URL_UPLOADS}${imagePath ?? ''}`;
	};

	const getContent: ConentContext['getContent'] = (slug, options) => {
		if (!checkIfExist(slug)) return slug;

		switch (content[slug].type) {
			case ContentType.TEXT:
				return getText(slug, options?.vars);
			case ContentType.ELEMENT:
				return getElement(slug, options) as string;
			case ContentType.IMAGE:
				return getImage(slug);
			default:
				return slug;
		}
	};

	const hasContent: ConentContext['hasContent'] = (slug) => checkIfExist(slug);

	const getErrorMessage: ConentContext['getErrorMessage'] = (codes) => {
		const codeToMessage = (code: ErrorCode | string) =>
			Object.values(ErrorCode).includes(code as ErrorCode)
				? getText(`error.${code}`)
				: '';

		const errorMessages = Array.isArray(codes)
			? codes.map((code) => codeToMessage(code))
			: [codeToMessage(codes)];

		const message =
			errorMessages.filter((m) => !!m).join('. ') ||
			codeToMessage(ErrorCode.GENERIC);

		return message;
	};

	const getMutationErrorMessage: ConentContext['getMutationErrorMessage'] = (
		error
	) => {
		if (isAppError(error)) getErrorMessage(error.stack);
		return getErrorMessage(ErrorCode.GENERIC);
	};

	return (
		<ContentContext
			value={{
				getContent,
				hasContent,
				language,
				setLanguage,
				getErrorMessage,
				getMutationErrorMessage,
				getEntityText,
			}}
		>
			{children}
		</ContentContext>
	);
};
